import { useTranslation } from "react-i18next";

import type { SymbolOverride } from "../data";

import { ChevronRightIcon, PlusIcon } from "../../../Icons";
import { Typo } from "../../../Typo";
import { formatScopePreview } from "./helpers";

/**
 * The split step's override affordance. Two shapes, driven purely by rule count:
 * a text link while there are none, and a summary card once there is at least
 * one — so the step shows what was configured without opening the sub-page.
 *
 * Sits outside the split block, at the step body's own spacing (the canvas
 * renders it as a sibling after `.split3`).
 */
export const OverrideEntry = ({
  rules,
  onOpen,
}: {
  rules: SymbolOverride[];
  onOpen: () => void;
}) => {
  const { t } = useTranslation();

  if (rules.length === 0) {
    return (
      <div>
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center gap-2 rounded text-text-brand-default hover:text-text-brand-bold hover:underline"
        >
          <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" />
          <Typo
            element="span"
            type="labelSM"
            textColor="text-text-brand-default"
          >
            {t("subIBReport_addOverrideRules", "Add override rule")}
          </Typo>
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={t("subIBReport_editOverrideRulesAria", "Edit override rules")}
      className="flex w-full items-center justify-between gap-4 rounded-lg border border-stroke-primary bg-surface-primary p-4 text-left transition-colors hover:bg-surface-tonal-default"
    >
      <span className="flex min-w-0 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <Typo element="span" type="labelSM" textColor="text-text-primary">
            {t("subIBReport_rebateSplitOverride", "Override rules")}
          </Typo>
          <span className="inline-flex items-center rounded-full bg-surface-accent-blue-subtler px-2 py-0.5">
            <Typo
              element="span"
              type="label2XS"
              textColor="text-text-accent-blue-boldest"
            >
              {t("subIBReport_overrideRuleCount", {
                count: rules.length,
                defaultValue_one: "{{count}} rule",
                defaultValue_other: "{{count}} rules",
              })}
            </Typo>
          </span>
        </span>
        <Typo element="span" type="bodyXS" textColor="text-text-secondary">
          {formatScopePreview(rules)}
        </Typo>
      </span>
      <ChevronRightIcon
        className="h-5 w-5 shrink-0 [&_path]:stroke-icon-tertiary"
        aria-hidden="true"
      />
    </button>
  );
};
